import React from "react";
import { useTranslation } from "react-i18next";
import { useHotel } from "@/hooks/useHotel";
import { toast } from "sonner";
import { PageState } from "@/components/common/PageState";
import { ConnectionStatusCard } from "./components/ConnectionStatusCard";
import { CredentialsForm } from "./components/CredentialsForm";
import { IntegrationToggle } from "./components/IntegrationToggle";
import { PmsConnectorCard } from "./components/PmsConnectorCard";
import { PmsSettingsForm } from "./components/PmsSettingsForm";
import {
  useGetPmsSettings,
  useGetCredentialsStatus,
  useUpdatePmsCredentials,
  useDeletePmsCredentials,
  useTestPmsConnection,
  useEnablePms,
  useDisablePms,
  useUpdatePmsSettings,
  useGetSyncStats,
} from "./hooks/pms.hooks";
import { OperaCredentialsFormData, SettingsFormData } from "./utils/pmsSchemas";
import { parseMissingOperaFields } from "./utils/operaConnectionErrors";
import { UpdatePmsCredentialsRequest } from "@/types/pms";
import { PmsType } from "@/types/hotel";
import { cn } from "@/lib/utils";

interface PmsSettingsPageProps {
  hotelId?: string | null;
  hotelProvider?: PmsType;
  canWrite?: boolean;
  className?: string;
}

export const PmsSettingsPage = ({
  hotelId: hotelIdOverride,
  hotelProvider,
  canWrite = true,
  className,
}: PmsSettingsPageProps = {}) => {
  const shouldUseHotelScope = hotelIdOverride === undefined;
  const { hotelId: scopedHotelId, hotel } = useHotel(shouldUseHotelScope);
  const resolvedHotelId =
    hotelIdOverride !== undefined ? hotelIdOverride : (scopedHotelId ?? null);
  const provider = hotelProvider ?? hotel?.pms;
  const { t } = useTranslation();
  const [operaMissingFields, setOperaMissingFields] = React.useState<
    (keyof OperaCredentialsFormData)[]
  >([]);

  const {
    data: settings,
    isLoading: settingsLoading,
    isError: settingsError,
  } = useGetPmsSettings(resolvedHotelId);
  const {
    data: credentials,
    isLoading: credentialsLoading,
    isError: credentialsError,
  } = useGetCredentialsStatus(resolvedHotelId);
  const { data: syncStats } = useGetSyncStats(resolvedHotelId);

  const updateCredentials = useUpdatePmsCredentials(resolvedHotelId);
  const deleteCredentials = useDeletePmsCredentials(resolvedHotelId);
  const testConnection = useTestPmsConnection(resolvedHotelId);
  const enablePms = useEnablePms(resolvedHotelId);
  const disablePms = useDisablePms(resolvedHotelId);
  const updateSettings = useUpdatePmsSettings(resolvedHotelId);

  if (!resolvedHotelId) {
    return <PageState text={t("pms.hotelNotSelected")} />;
  }

  if (settingsLoading || credentialsLoading) {
    return <PageState text={t("pms.loading")} />;
  }

  if (settingsError || credentialsError) {
    return <PageState text={t("pms.loadingError")} tone="error" />;
  }

  const hasCredentials = credentials?.hasCredentials ?? false;

  const handleSaveCredentials = (data: UpdatePmsCredentialsRequest) => {
    if (!canWrite) return;

    setOperaMissingFields([]);
    updateCredentials.mutate(data, {
      onSuccess: () => toast.success(t("pms.credentials.saveSuccess")),
      onError: () => toast.error(t("pms.credentials.saveError")),
    });
  };

  const handleDeleteCredentials = () => {
    if (!canWrite) return;

    deleteCredentials.mutate(undefined, {
      onSuccess: () => toast.success(t("pms.credentials.deleteSuccess")),
      onError: () => toast.error(t("pms.credentials.deleteError")),
    });
  };

  const handleTestConnection = () => {
    if (!canWrite) return;

    testConnection.mutate(undefined, {
      onSuccess: (result) => {
        if (result.success) {
          setOperaMissingFields([]);
          toast.success(
            t("pms.connection.success", { message: result.message }),
          );
        } else {
          setOperaMissingFields(parseMissingOperaFields(result.message));
          toast.error(t("pms.connection.failure", { message: result.message }));
        }
      },
      onError: () => {
        toast.error(
          t("pms.connection.failure", {
            message: t("common.unknownError"),
          }),
        );
      },
    });
  };

  const handleEnable = () => {
    if (!canWrite) return;

    enablePms.mutate(
      { pollIntervalMinutes: settings?.pollIntervalMinutes ?? 15 },
      {
        onSuccess: () => toast.success(t("pms.toggle.enableSuccess")),
        onError: () => toast.error(t("pms.toggle.enableError")),
      },
    );
  };

  const handleDisable = () => {
    if (!canWrite) return;

    disablePms.mutate(undefined, {
      onSuccess: () => toast.success(t("pms.toggle.disableSuccess")),
      onError: () => toast.error(t("pms.toggle.disableError")),
    });
  };

  const handleSaveSettings = (data: SettingsFormData) => {
    if (!canWrite) return;

    updateSettings.mutate(data, {
      onSuccess: () => toast.success(t("pms.settings.saveSuccess")),
      onError: () => toast.error(t("pms.settings.saveError")),
    });
  };

  return (
    <div className={cn("flex max-w-2xl flex-col gap-6 p-12", className)}>
      <PmsConnectorCard
        settings={settings ?? null}
        credentials={credentials ?? null}
        hotelProvider={provider}
        canWrite={canWrite}
        isEnabling={enablePms.isPending}
        isDisabling={disablePms.isPending}
        onEnable={handleEnable}
        onDisable={handleDisable}
      />

      <ConnectionStatusCard
        settings={settings ?? null}
        credentials={credentials ?? null}
        recentActivity={syncStats?.recentActivity}
      />

      <CredentialsForm
        hasCredentials={hasCredentials}
        hotelProvider={provider}
        settings={settings ?? null}
        credentials={credentials ?? null}
        operaMissingFields={operaMissingFields}
        canWrite={canWrite}
        isSaving={updateCredentials.isPending}
        isDeleting={deleteCredentials.isPending}
        isTesting={testConnection.isPending}
        onSave={handleSaveCredentials}
        onDelete={handleDeleteCredentials}
        onTest={handleTestConnection}
      />

      {hasCredentials && (
        <PmsSettingsForm
          settings={settings ?? null}
          canWrite={canWrite}
          isSaving={updateSettings.isPending}
          onSave={handleSaveSettings}
        />
      )}

      {hasCredentials && (
        <IntegrationToggle
          enabled={settings?.enabled ?? false}
          canWrite={canWrite}
          isEnabling={enablePms.isPending}
          isDisabling={disablePms.isPending}
          onEnable={handleEnable}
          onDisable={handleDisable}
        />
      )}
    </div>
  );
};
